import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAttribution } from "@/utils/attribution";
import { ArrowLeft, ArrowRight, CheckCircle, Calendar, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: "window-count", title: "Window Screens" },
  { id: "window-frames", title: "Window Frames" },
  { id: "door-count", title: "Screen Doors" },
  { id: "door-frames", title: "Door Frames" },
  { id: "notes", title: "Additional Info" },
  { id: "contact", title: "Contact Info" },
  { id: "address", title: "Location" },
  { id: "schedule", title: "Schedule" },
  { id: "complete", title: "Complete" }
];

export default function Intake() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [formData, setFormData] = useState({
    window_count: "",
    window_frames: "",
    door_count: "",
    door_frames: "",
    notes: "",
    full_name: "",
    phone: "",
    email: "",
    address: "",
    zip_code: "",
    preferred_date: "",
    preferred_time: "",
    gclid: "",
    fbclid: ""
  });

  useEffect(() => {
    // Capture GCLID and FBCLID from URL
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get('gclid');
    const fbclid = params.get('fbclid');
    
    if (gclid || fbclid) {
      setFormData(prev => ({
        ...prev,
        gclid: gclid || "",
        fbclid: fbclid || ""
      }));
    }
  }, []);

  useEffect(() => {
    if (step === 7 && formData.zip_code && formData.preferred_date) {
      fetchAvailableSlots();
    }
  }, [step, formData.zip_code, formData.preferred_date]);

  // Initialize Google Maps Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (window.google && window.google.maps && addressInputRef.current && !autocompleteRef.current) {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          componentRestrictions: { country: "us" },
          fields: ["address_components", "formatted_address"],
          types: ["address"]
        });

        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();
          if (place && place.address_components) {
            let zipCode = "";
            let streetNumber = "";
            let route = "";
            let city = "";
            let state = "";

            for (const component of place.address_components) {
              const type = component.types[0];
              if (type === "postal_code") {
                zipCode = component.long_name;
              } else if (type === "street_number") {
                streetNumber = component.long_name;
              } else if (type === "route") {
                route = component.long_name;
              } else if (type === "locality") {
                city = component.long_name;
              } else if (type === "administrative_area_level_1") {
                state = component.short_name;
              }
            }

            const fullAddress = place.formatted_address || `${streetNumber} ${route}, ${city}, ${state}`.trim();
            
            setFormData(prev => ({
              ...prev,
              address: fullAddress,
              zip_code: zipCode || prev.zip_code
            }));
          }
        });
        setGoogleMapsLoaded(true);
      }
    };

    // Check if Google Maps is already loaded
    if (window.googleMapsReady) {
      initAutocomplete();
    } else {
      // Set up callback for when Google Maps loads
      window.onGoogleMapsReady = initAutocomplete;
    }

    return () => {
      window.onGoogleMapsReady = null;
    };
  }, [step]);

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(`${API}/intake/available-slots`, {
        params: {
          zip_code: formData.zip_code,
          date: formData.preferred_date
        }
      });
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error("Failed to fetch slots:", error);
    }
  };

  const handleNext = () => {
    // Skip window frames question if no windows
    if (step === 0 && (!formData.window_count || formData.window_count === "0")) {
      setStep(2); // Skip to door count
      return;
    }
    
    // Skip door frames question if no doors
    if (step === 2 && (!formData.door_count || formData.door_count === "0")) {
      setStep(4); // Skip to notes
      return;
    }
    
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    // Skip window frames when going back if no windows
    if (step === 2 && (!formData.window_count || formData.window_count === "0")) {
      setStep(0); // Go back to window count
      return;
    }
    
    // Skip door frames question when going back if no doors
    if (step === 4 && (!formData.door_count || formData.door_count === "0")) {
      setStep(2); // Go back to door count
      return;
    }
    
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build service description from selections
      const windowDesc = formData.window_count > 0 
        ? `${formData.window_count} window screens (${formData.window_frames})` 
        : "";
      const doorDesc = formData.door_count > 0 
        ? `${formData.door_count} screen doors (${formData.door_frames})` 
        : "";
      const serviceDesc = [windowDesc, doorDesc].filter(Boolean).join(", ");
      
      await axios.post(`${API}/intake/submit`, {
        service_type: "custom",
        screen_count: parseInt(formData.window_count || 0) + parseInt(formData.door_count || 0),
        screen_type: "custom",
        full_name: formData.full_name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        zip_code: formData.zip_code,
        notes: `${serviceDesc}. ${formData.notes || ""}`.trim(),
        preferred_date: formData.preferred_date || null,
        preferred_time: formData.preferred_time || null,
        gclid: formData.gclid || null,
        fbclid: formData.fbclid || null,
        ...getAttribution()
      });
      
      // Fire conversion events
      if (window.gtag && formData.gclid) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL',
          'transaction_id': ''
        });
      }
      
      // Fire Meta Pixel Lead event
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Screen Repair Intake',
          content_category: formData.window_count > 0 ? 'Window Screens' : 'Screen Doors',
          value: parseInt(formData.window_count || 0) + parseInt(formData.door_count || 0),
          currency: 'USD'
        });
      }
      
      setStep(STEPS.length - 1);
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return formData.window_count && parseInt(formData.window_count) >= 0;
      case 1: return formData.window_frames;
      case 2: return formData.door_count !== ""; // Allow 0
      case 3: return formData.door_frames;
      case 4: return true; // Notes optional
      case 5: return formData.full_name && formData.phone && formData.email;
      case 6: return formData.address && formData.zip_code;
      case 7: return true; // Schedule is optional
      default: return true;
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  if (step === STEPS.length - 1) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-green-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="w-20 h-20 text-[#207024] mx-auto mb-6" />
          <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase mb-4">Thank You!</h1>
          <p className="font-inter text-lg text-zinc-700 mb-8">
            We've received your request and will contact you soon to confirm your appointment.
          </p>
          <button
            onClick={() => window.location.href = "https://screenzz.com"}
            className="h-14 px-8 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold text-lg"
          >
            Back to ScreenzZ.com
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b-2 border-zinc-100 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_window-screen-mgmt/artifacts/xq82sr3z_ScreenzZ-1024x1024.png" 
            alt="ScreenzZ" 
            className="h-12"
          />
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-zinc-100 h-2">
        <div 
          className="bg-[#207024] h-2 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-8">
          <p className="font-inter text-sm text-zinc-500 mb-2">
            Step {step + 1} of {STEPS.length - 1}
          </p>
          <h2 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
            {STEPS[step].title}
          </h2>
        </div>

        {/* Step 0: Window Screen Count */}
        {step === 0 && (
          <div className="space-y-6">
            <p className="font-inter text-lg text-zinc-600 mb-6">
              How many window screens do you need?
            </p>
            <input
              type="number"
              min="0"
              max="50"
              value={formData.window_count}
              onChange={(e) => setFormData({ ...formData, window_count: e.target.value })}
              placeholder="Enter number"
              className="w-full h-20 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-3xl px-6 text-center font-bold"
            />
            <div className="grid grid-cols-4 gap-3">
              {[0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20].map((num) => (
                <button
                  key={num}
                  onClick={() => setFormData({ ...formData, window_count: num.toString() })}
                  className={`h-16 rounded-lg border-2 font-bold text-xl transition-all ${
                    formData.window_count === num.toString()
                      ? "border-[#207024] bg-green-50"
                      : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Window Frames */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="font-inter text-lg text-zinc-600 mb-6">
              Do you need new frames or just your screens replaced?
            </p>
            {[
              { value: "new_frames", label: "Yes, I need new frames" },
              { value: "screens_only", label: "No, just the screens" },
              { value: "mixed", label: "Some need frames, others don't" }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFormData({ ...formData, window_frames: option.value })}
                className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                  formData.window_frames === option.value
                    ? "border-[#207024] bg-green-50"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                <h3 className="font-inter text-xl font-bold text-zinc-900">
                  {option.label}
                </h3>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Door Screen Count */}
        {step === 2 && (
          <div className="space-y-6">
            <p className="font-inter text-lg text-zinc-600 mb-6">
              How many screen doors do you need?
            </p>
            <input
              type="number"
              min="0"
              max="20"
              value={formData.door_count}
              onChange={(e) => setFormData({ ...formData, door_count: e.target.value })}
              placeholder="Enter number"
              className="w-full h-20 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-3xl px-6 text-center font-bold"
            />
            <div className="grid grid-cols-4 gap-3">
              {[0, 1, 2, 3, 4, 5, 6, 8].map((num) => (
                <button
                  key={num}
                  onClick={() => setFormData({ ...formData, door_count: num.toString() })}
                  className={`h-16 rounded-lg border-2 font-bold text-xl transition-all ${
                    formData.door_count === num.toString()
                      ? "border-[#207024] bg-green-50"
                      : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Door Frames (only if door_count > 0) */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="font-inter text-lg text-zinc-600 mb-6">
              Do you need new screen doors or just rescreening?
            </p>
            {[
              { value: "brand_new", label: "Yes, I need a brand new screen door" },
              { value: "rescreen_only", label: "No, just the door rescreened" }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFormData({ ...formData, door_frames: option.value })}
                className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                  formData.door_frames === option.value
                    ? "border-[#207024] bg-green-50"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                <h3 className="font-inter text-xl font-bold text-zinc-900">
                  {option.label}
                </h3>
              </button>
            ))}
          </div>
        )}

        {/* Step 4: Additional Notes */}
        {step === 4 && (
          <div className="space-y-4">
            <p className="font-inter text-lg text-zinc-600 mb-6">
              Is there anything else we should know? (Optional)
            </p>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any special instructions, access codes, or details about your screens..."
              rows={6}
              className="w-full rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg p-6"
            />
          </div>
        )}

        {/* Step 5: Contact Info */}
        {step === 5 && (
          <div className="space-y-4">
            <div>
              <label className="font-inter font-semibold text-zinc-700 block mb-2">Full Name *</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Smith"
                className="w-full h-14 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg px-6"
              />
            </div>
            <div>
              <label className="font-inter font-semibold text-zinc-700 block mb-2">Phone Number *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="w-full h-14 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg px-6"
              />
            </div>
            <div>
              <label className="font-inter font-semibold text-zinc-700 block mb-2">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                className="w-full h-14 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg px-6"
              />
            </div>
          </div>
        )}

        {/* Step 6: Address */}
        {step === 6 && (
          <div className="space-y-4">
            <div>
              <label className="font-inter font-semibold text-zinc-700 block mb-2 flex items-center gap-2">
                Street Address *
                {googleMapsLoaded && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Autocomplete enabled
                  </span>
                )}
              </label>
              <input
                ref={addressInputRef}
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Start typing your address..."
                className="w-full h-14 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg px-6"
                data-testid="address-input"
              />
              {!googleMapsLoaded && (
                <p className="text-xs text-zinc-500 mt-1">
                  Enter your full address (e.g., 123 Main St, San Francisco, CA)
                </p>
              )}
            </div>
            <div>
              <label className="font-inter font-semibold text-zinc-700 block mb-2">Zip Code *</label>
              <input
                type="text"
                maxLength={5}
                pattern="[0-9]{5}"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                placeholder="94102"
                className="w-full h-14 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg px-6"
                data-testid="zip-code-input"
              />
              {googleMapsLoaded && formData.zip_code && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Zip code auto-filled from address
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 7: Schedule */}
        {step === 7 && (
          <div className="space-y-6">
            <p className="font-inter text-zinc-600">
              Pick a preferred date and time (optional). We'll confirm availability with you.
            </p>
            
            <div>
              <label className="font-inter font-semibold text-zinc-700 block mb-2 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Preferred Date
              </label>
              <input
                type="date"
                value={formData.preferred_date}
                onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full h-14 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg px-6"
              />
            </div>

            {formData.preferred_date && availableSlots.length > 0 && (
              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Available Times
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.time}
                      onClick={() => setFormData({ ...formData, preferred_time: slot.time })}
                      className={`h-16 rounded-xl border-2 font-bold text-lg transition-all ${
                        formData.preferred_time === slot.time
                          ? "border-[#207024] bg-green-50"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {formData.preferred_date && availableSlots.length === 0 && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800">
                  No available slots for this date. We'll contact you to schedule a convenient time.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-4 mt-8 pt-8 border-t-2 border-zinc-100">
          {step > 0 && step < STEPS.length - 1 && (
            <button
              onClick={handleBack}
              className="h-14 px-8 rounded-xl border-2 border-zinc-200 text-zinc-900 font-bold text-lg hover:bg-zinc-50 flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          )}
          
          {step < 7 ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="h-14 flex-1 rounded-xl bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="h-14 flex-1 rounded-xl bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? "Submitting..." : "Submit Request"}
              <CheckCircle className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
